import React, { useState, useEffect } from 'react';
import { CreditCard, Banknote, Hash, Loader2, Disc, Star, ChevronDown, User, Wallet } from 'lucide-react'; 
import Layout from './Layout';
import PaymentModal from './PaymentModal';

const HistoryPage = ({ onNav }) => {
  const [apiSessions, setApiSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('open');
  const [closingSession, setClosingSession] = useState(null);
  
  // New States for Dancer filtering functionality
  const [dancersList, setDancersList] = useState([]);
  const [selectedDancer, setSelectedDancer] = useState('all');

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://companylocker.com/admin/wp-json/dpt/v1/history');
      const data = await response.json();
      setApiSessions(data);
    } catch (err) {
      console.error("Failed to fetch history:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch dancers list from the total-today endpoint
  const fetchDancers = async () => {
    try {
      const response = await fetch('https://companylocker.com/admin/wp-json/dpt/v1/total-today');
      const data = await response.json();
      if (data.success && data.subscribers) {
        setDancersList(data.subscribers);
      }
    } catch (err) {
      console.error("Error fetching dancers list:", err);
    }
  };

  useEffect(() => {
    fetchHistory();
    fetchDancers();
  }, []);

  const formatTime = (ts) => {
    if (!ts) return "--:--";
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Central filtering logic matching both status tab and selected dancer identity
  const filteredSessions = apiSessions.filter((s) => {
    const matchesTab = s.status === tab;
    if (tab === 'closed' && selectedDancer !== 'all') {
      return matchesTab && (s.dancer === selectedDancer || s.user_id?.toString() === selectedDancer);
    }
    return matchesTab;
  });

  // NEW: Dynamic aggregations matching exactly what is currently filtered on screen
  const filteredTotals = filteredSessions.reduce((acc, curr) => {
    acc.payout += parseFloat(curr.tip_amount || 0);
    acc.count += 1;
    return acc;
  }, { payout: 0, count: 0 });

  return (
    <Layout activeTab="history" onNav={onNav}>
      {/* Tab Selectors */}
      <div className="flex gap-4 pt-8 pb-4">
        <button
          onClick={() => setTab('open')}
          className={`flex-1 py-3 rounded-2xl text-[10px] font-black tracking-widest transition-all ${
            tab === 'open' ? 'bg-purple-600 shadow-lg shadow-purple-500/20 text-white' : 'bg-white/5 text-gray-500'
          }`}
        >
          OPEN
        </button>
        <button
          onClick={() => setTab('closed')}
          className={`flex-1 py-3 rounded-2xl text-[10px] font-black tracking-widest transition-all ${
            tab === 'closed' ? 'bg-purple-600 shadow-lg shadow-purple-500/20 text-white' : 'bg-white/5 text-gray-500'
          }`}
        >
          CLOSED
        </button>
      </div>

      {/* Conditional Dancer Filter Dropdown */}
      {tab === 'closed' && (
        <div className="mb-4 relative animate-fadeIn">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-purple-500 pointer-events-none">
            <User size={16} />
          </div>
          <select
            className="w-full bg-[#151525] border border-white/10 rounded-2xl pl-11 pr-10 py-4 outline-none text-xs font-black text-white appearance-none uppercase tracking-wider focus:border-purple-500 transition-all"
            value={selectedDancer}
            onChange={(e) => setSelectedDancer(e.target.value)}
          >
            <option value="all">SHOW ALL DANCERS</option>
            {dancersList.map((dancer) => (
              <option key={dancer.id} value={dancer.name} className="bg-[#0a0a1a]">
                {dancer.name.toUpperCase()}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-5 top-1/2 -translate-y-1/2 text-purple-500 pointer-events-none" size={16} />
        </div>
      )}

      {/* NEW: Dynamic Summary Statistics Block */}
      {tab === 'closed' && !loading && (
        <div className="mb-6 bg-gradient-to-r from-purple-900/20 to-pink-900/10 border border-purple-500/10 rounded-3xl p-5 flex justify-between items-center shadow-lg backdrop-blur-md">
          <div className="flex items-center gap-3">
            <div className="bg-purple-500/10 p-3 rounded-2xl border border-purple-500/20 text-purple-400">
              <Wallet size={20} />
            </div>
            <div>
              <p className="text-[9px] font-black uppercase text-gray-400 tracking-widest">
                {selectedDancer === 'all' ? 'Total Dancer Payouts' : 'Dancer Payout'}
              </p>
              <h4 className="text-2xl font-black text-white tracking-tight">
                ${filteredTotals.payout.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </h4>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[9px] font-black uppercase text-gray-400 tracking-widest mb-0.5">Sessions</p>
            <span className="px-3 py-1 bg-white/5 rounded-full border border-white/10 text-xs font-black text-pink-400">
              {filteredTotals.count}
            </span>
          </div>
        </div>
      )}

      {/* Main Container Stream */}
      <div className="space-y-4 pb-24">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-500 gap-3">
            <Loader2 className="animate-spin text-purple-500" size={32} />
            <span className="text-[10px] font-black uppercase tracking-widest">Loading Records...</span>
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="text-center py-20 text-gray-600 text-[10px] font-black uppercase tracking-widest">
            No {tab} sessions found {selectedDancer !== 'all' && 'for this dancer'}
          </div>
        ) : (
          filteredSessions.map((s) => (
            <div key={s.id} className="bg-[#0a0a1a] p-5 rounded-[32px] border border-white/5 shadow-xl">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-black text-white">{s.room}</h3>
                  <p className="text-xs text-purple-400 font-bold">{s.dancer}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="text-[9px] font-black px-2 py-1 bg-white/5 rounded-md border border-white/10 text-gray-300">
                    {s.sessionType} MIN
                  </span>
                  {tab === 'open' && (
                    <div className="flex items-center gap-1 text-[9px] font-black text-purple-500 uppercase">
                      <Disc size={10} /> <span>{s.dance || 1} DANCES</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-1 mb-4 text-[10px] font-bold text-gray-500 uppercase">
                <div className="flex justify-between">
                  <span>Start:</span>
                  <span className="text-white">{formatTime(s.start_time)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Sched End:</span>
                  <span className="text-pink-500">{formatTime(s.end_time)}</span>
                </div>
                {tab === 'closed' && (
                  <div className="flex justify-between text-purple-400 border-t border-white/5 pt-1 mt-1">
                    <span>Actual End:</span>
                    <span>{formatTime(s.actual_end)}</span>
                  </div>
                )}
              </div>

              {tab === 'open' ? (
                <button
                  onClick={() => setClosingSession(s)}
                  className="w-full py-4 bg-red-600/10 text-red-500 border border-red-500/20 rounded-2xl text-[10px] font-black uppercase tracking-widest active:scale-95 transition-all"
                >
                  END SESSION
                </button>
              ) : (
                <div className="space-y-3">
                  {/* Primary Amount Row */}
                  <div className="flex justify-between items-center bg-white/5 p-4 rounded-2xl border border-white/5">
                    <div className="flex items-center gap-2">
                      {s.type === 'card' ? (
                        <CreditCard size={16} className="text-blue-400" />
                      ) : (
                        <Banknote size={16} className="text-green-400" />
                      )}
                      <span className="text-base font-black text-white">${s.amount}</span>
                    </div>
                    
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-[10px] text-green-500 font-black tracking-widest uppercase">
                         Dancer Payout : ${s.tip_amount || 0}
                      </span>
                      {s.extra_charge && parseFloat(s.extra_charge) > 0 && (
                        <div className="flex items-center gap-1 text-yellow-500">
                          <Star size={10} className="fill-yellow-500" />
                          <span className="text-[9px] font-black uppercase">Entertainer Tip: ${s.extra_charge}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Metadata Row */}
                  <div className="flex items-center gap-6 px-2">
                    <div className="flex items-center gap-2 text-[9px] font-black text-gray-500 uppercase tracking-widest">
                      <Disc size={14} className="text-purple-500" /> 
                      Dances: <span className="text-white">{s.dance || 0}</span>
                    </div>
                    
                    {s.type === 'card' && s.ref_number && (
                      <div className="flex items-center gap-2 text-[9px] font-black text-gray-500 uppercase tracking-widest border-l border-white/10 pl-4">
                        <Hash size={14} className="text-blue-500" /> 
                        Ref: <span className="text-white">{s.ref_number}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {closingSession && (
        <PaymentModal
          session={closingSession}
          onCancel={() => setClosingSession(null)}
          onSave={(data) => {
            setClosingSession(null);
            fetchHistory(); 
            setTab('closed');
          }}
        />
      )}
    </Layout>
  );
};

export default HistoryPage;